import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AdvisorClientForm from './advisors/AdvisorClientForm';

const AdvisorPortal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [advisorInfo, setAdvisorInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ client_name: '', client_email: '', note: '', professional_type: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    validateToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/advisor-portal/validate-token/${token}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Invalid token');
      setAdvisorInfo(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/advisor-portal/introduce-client?token=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send introduction');
      setSuccess('Introduction sent successfully!');
      setFormData({ client_name: '', client_email: '', note: '', professional_type: '' });
      validateToken();
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const getSubmitButtonText = () => {
    if (sending) return 'Sending Introduction...';
    if (advisorInfo?.limit_info?.remaining === 0) return 'Weekly Limit Reached';
    return 'Send Private Introduction';
  };

  if (loading) return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
    </div>
  );

  if (error && !advisorInfo) return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-800 rounded-2xl p-8 border border-gray-700 text-center">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Invalid or Expired Link</h2>
        <p className="text-gray-400 mb-6">{error}</p>
        <button onClick={() => navigate('/')} className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition">Return to Home</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black">
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Trusted Professional Private Introductions</h1>
              <p className="text-gray-400 mt-1">Welcome, {advisorInfo?.advisor_name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Introductions This Week</p>
              <p className="text-2xl font-bold text-white">{advisorInfo?.limit_info?.count || 0} / {advisorInfo?.limit_info?.limit || 3}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {advisorInfo?.preview && (
          <div
            className="border border-amber-400/60 bg-amber-500/10 rounded-lg p-3 mb-6 text-xs text-amber-200 leading-relaxed flex items-start gap-2"
            data-testid="adv-preview-banner"
          >
            <span className="font-semibold uppercase tracking-wide text-amber-300 mr-1">PREVIEW MODE</span>
            <span>
              This is a preview link for staff training or a demo. No real introduction will be created and no
              emails will be sent. This link auto-expires in 1 hour.
            </span>
          </div>
        )}
        <div className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-700">
          <p className="text-gray-300 mb-4">This Portal allows Approved Trusted Professionals to Privately Introduce Clients to MyInvestFolio.</p>
          <p className="text-gray-400 text-sm">MyInvestFolio is private, independent portfolio infrastructure for discerning investors — a personal investment visibility platform, not an advisory system.</p>
        </div>
        <AdvisorClientForm
          formData={formData} handleChange={handleChange} handleSubmit={handleSubmit}
          sending={sending} error={error} success={success} advisorInfo={advisorInfo}
          getSubmitButtonText={getSubmitButtonText}
          availableTypes={advisorInfo?.available_types || []}
        />
        {success && (
          <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-green-500">
            <h3 className="text-xl font-bold text-white mb-2">Introduction Sent</h3>
            <p className="text-gray-300">Your introduction has been sent successfully.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvisorPortal;
